import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import BuildRoundedIcon from '@mui/icons-material/BuildRounded'
import CloseIcon from '@mui/icons-material/Close'
import API from '../../../../services/API'
import { GlobalContext, TenantSettingsContext } from '../../../../App'
import BigModal from '../../../../components/Modals/BigModal'
import AnDateField from '../../../../components/DateTime/DateField'
import { getCurrencySymbol } from '../../../../helpers/currency'
import { serviceModalStyle } from '../../vehicles/VehicleComponents/ServicingMotInsurance/AddService'
import { AssetContext } from './SingleAsset'

const MAINTENANCE_TYPES = ['Routine Service', 'Repair']

// Same header / scrolling body / footer shell as the vehicle Add Service
// popup so the two record-keeping forms feel like one product.
// `prefill` seeds the form from a planned downtime window, and
// `plannedUnavailabilityId` makes the save mark that window completed.
const RecordMaintenance = ({
  open,
  onClose,
  onSaved,
  prefill,
  plannedUnavailabilityId,
}) => {
  const { asset } = useContext(AssetContext)
  const { setToast } = useContext(GlobalContext)
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig

  const [maintenanceDate, setMaintenanceDate] = useState(moment())
  const [type, setType] = useState(MAINTENANCE_TYPES[0])
  const [serviceProvider, setServiceProvider] = useState('')
  const [expiry, setExpiry] = useState(moment().add(1, 'year'))
  const [description, setDescription] = useState('')
  const [cost, setCost] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  // Fresh form each time it opens
  useEffect(() => {
    if (!open) return
    const startDate = prefill?.maintenanceDate
      ? moment(prefill.maintenanceDate)
      : moment()
    setMaintenanceDate(startDate)
    setType(MAINTENANCE_TYPES[0])
    setServiceProvider('')
    setExpiry(moment(startDate).add(1, 'year'))
    setDescription(prefill?.description ?? '')
    setCost('')
    setSaving(false)
    setError(null)
  }, [open, prefill])

  const handleSubmit = (e) => {
    e.preventDefault()
    if (saving) return
    setSaving(true)
    setError(null)
    API.post(`/maintenancelog?assetID=${asset._id}`, {
      maintenanceDate,
      type,
      serviceProvider,
      expiry,
      description,
      cost: cost === '' ? undefined : parseFloat(cost),
      asset: asset._id,
      ...(plannedUnavailabilityId ? { plannedUnavailabilityId } : {}),
    })
      .then((res) => {
        if (res.status === 200) {
          setToast({ message: 'Maintenance log recorded' })
          onSaved?.()
          onClose()
        } else {
          setError('Unable to save this maintenance log. Please try again.')
          setSaving(false)
        }
      })
      .catch(() => {
        setError('Unable to save this maintenance log. Please try again.')
        setSaving(false)
      })
  }

  const datesValid =
    moment(maintenanceDate).isValid() && moment(expiry).isValid()

  return (
    <BigModal open={open} onClose={onClose} style={serviceModalStyle}>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}
      >
        {/* ---- Header ---- */}
        <Box
          sx={{
            flexShrink: 0,
            bgcolor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
            px: { xs: 2, sm: 3 },
            py: 2,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <BuildRoundedIcon />
            </Box>
            <Box flex={1} minWidth={0}>
              <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                Record maintenance
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {[asset.assetID, asset.subCategory || asset.category]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
            </Box>
            <Tooltip title="Close">
              <span>
                <IconButton
                  onClick={onClose}
                  size="small"
                  aria-label="Close"
                  disabled={saving}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Box>

        {/* ---- Body ---- */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            bgcolor: 'background.default',
          }}
        >
          <Stack spacing={2} sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            <Paper
              variant="outlined"
              sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <AnDateField
                    date={maintenanceDate}
                    onChange={(value) => {
                      setMaintenanceDate(value)
                      // Default validity of a year from the work; the expiry
                      // stays editable below.
                      if (value && moment(value).isValid()) {
                        setExpiry(moment(value).add(1, 'year'))
                      }
                    }}
                    label="Maintenance date"
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={type}
                      onChange={(e) => setType(e.target.value)}
                      label="Type"
                    >
                      {MAINTENANCE_TYPES.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    value={serviceProvider}
                    onChange={(e) => setServiceProvider(e.target.value)}
                    label="Service provider"
                    autoComplete="off"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <AnDateField
                    date={expiry}
                    onChange={(value) => setExpiry(value)}
                    label="Valid until"
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    type="number"
                    value={cost}
                    onChange={(e) => setCost(e.target.value)}
                    label="Cost"
                    autoComplete="off"
                    fullWidth
                    InputProps={{
                      inputProps: { min: 0, step: 0.01 },
                      startAdornment: (
                        <InputAdornment position="start">
                          {getCurrencySymbol(orgSettings?.currency)}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    label="Work completed"
                    placeholder="e.g. Annual calibration and battery replacement"
                    autoComplete="off"
                    fullWidth
                    multiline
                    minRows={3}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Stack>
        </Box>

        {/* ---- Footer ---- */}
        <Box
          sx={{
            flexShrink: 0,
            bgcolor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
            px: { xs: 2, sm: 3 },
            py: 1.5,
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ sm: 'center' }}
            spacing={1.5}
          >
            <Typography variant="caption" color="text.secondary">
              Fields marked * are required
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={onClose} color="inherit" disabled={saving}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={!datesValid || saving}
              >
                {saving ? 'Saving…' : 'Record maintenance'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </BigModal>
  )
}

export default RecordMaintenance
